import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { ProfileService } from '../../services/profile.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { AuthService } from '../../services/auth.service';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';

/**
 * Manages the password change UI and workflow: validates form input, submits password changes to the server, shows user feedback, and triggers a logout and page block sequence after a successful change.
 * This standalone component provides a reactive form for current, new and confirmation passwords, performs client-side matching validation, handles submit errors, and emits a cancel event when editing is cancelled.
 * Key methods:
 *   - passwordMatchValidator(control): custom validator enforcing newPassword === confirmPassword.
 *   - onEdit(): enables edit mode for the form.
 *   - onCancel(): resets the form, disables edit mode and emits cancelClicked.
 *   - onSubmit(): validates and submits the password change via ProfileService, displays success/error messages via NotificationService, and on success schedules a user logout and blocks the page.
 * Lifecycle hooks: this component does not implement Angular lifecycle hooks (ngOnInit/ngOnDestroy) explicitly; initialization is performed in the constructor.
 * @component
 * @selector app-password-settings
 * @example
 *   <app-password-settings (cancelClicked)="handleCancel()"></app-password-settings>
 * @input none - This component has no input properties; it manages its own reactive form internally.
 * @output EventEmitter<void> cancelClicked - Emitted when the user cancels editing (or when the component resets after success) to notify parent components.
 */
@Component({
  selector: 'app-password-settings',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterModule],
  templateUrl: './password-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './password-settings.component.scss',
})
export class PasswordSettingsComponent {
  passwordForm: FormGroup;
  isEditing = false;
  isBlocking = false;
  @Output() cancelClicked: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Initializes the PasswordSettingsComponent and creates the passwordForm FormGroup with validation rules.
   * @example
   * new PasswordSettingsComponent(fb, profileService, messageService, authService, router)
   * PasswordSettingsComponent instance
   * @param {{FormBuilder}} {{fb}} - FormBuilder used to construct reactive form controls and groups.
   * @param {{ProfileService}} {{profileService}} - Service used to manage and persist user profile data.
   * @param {{NotificationService}} {{messageService}} - Service used to display user-facing messages and notifications.
   * @param {{AuthService}} {{authService}} - Service used to access authentication and user credentials.
   * @param {{Router}} {{router}} - Angular Router used for navigation after actions.
   * @returns {{void}} Initializes the component and sets up the password form; does not return a value.
   **/
  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private messageService: NotificationService,
    private authService: AuthService,
    private router: Router
  ) {
    this.passwordForm = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { mismatch: true };
  }

  onEdit() {
    this.isEditing = true;
  }

  onCancel() {
    this.isEditing = false;
    this.passwordForm.reset();
    this.cancelClicked.emit();
  }

  /**
   * Submit password change: validates the password form, calls the change password API, shows success/error toasts, blocks the UI and logs out after success.
   * @example
   * nSubmit()
   * undefined
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} Triggers side effects (API request, toast messages, UI blocking and logout) and returns nothing.
   **/
  onSubmit() {
    if (this.passwordForm.valid) {
      const { currentPassword, newPassword } = this.passwordForm.value;
      this.profileService
        .changePassword({
          current_password: currentPassword,
          new_password: newPassword,
        })
        .subscribe({
          next: response => {
            this.messageService.add({
              severity: 'success',
              summary: 'Password changed!',
              detail: 'Your password has been changed successfully',
            });
            this.onCancel();

            // Wait 2 seconds, then show logout message and block page
            setTimeout(() => {
              this.messageService.add({
                severity: 'info',
                summary: 'Login Required',
                detail: 'You need to login again to continue',
                life: 3000,
              });

              // Block the entire page
              this.isBlocking = true;

              // Wait for toast to be visible, then logout
              setTimeout(() => {
                this.authService.logout();
              }, 1000);
            }, 2000);
          },
          error: err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: sanitizeErrorMessage(
                err.error?.message || 'An error occurred while changing your password.'
              ),
            });
          },
        });
    }
  }
}
